import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { useChapterStore, type ViewMode } from "@/zapi-frontend/stores/chapter";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { CaretRight, EyeIcon, ListIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { useProjectSidebarStore } from "@/zapi-frontend/stores/projectSidebarStore";
import { getChapterTools } from "../ProjectLayout/lib/chapterTools";
import {
  DEEP_DIVE_SECTIONS,
  EXEC_SUMMARY_SLUG,
  FULL_SUMMARY_SLUG,
  isAppendixChapter
} from "../ProjectSidebar/ProjectSidebar";
import { getExecSummaryLabelKey } from "@/zapi-frontend/shared/lib/projectChapters";
import { useMediaQuery } from "@/zapi-frontend/shared/hooks/useMediaQuery";
import { useWorkspaceLayout } from "@/zapi-frontend/shared/hooks/useWorkspaceLayout";
import { DashboardHomeIcon } from "./_DashboardHomeIcon";
import { LayoutToggle } from "./_LayoutToggle";
import { MemoOptionsMenu } from "./_MemoOptionsMenu";
import { ProjectInfoTooltip } from "./_ProjectInfoTooltip";
import { useProjectGenerationState } from "@/zapi-frontend/shared/hooks/useProjectGenerationState";

const NON_CHAPTER_VIEWS = new Set<ViewMode>([
  "dashboard",
  "projectOverview",
  "projectFrame",
  "projectConfig",
  "vault",
  "valueChain",
  "exports"
]);

type TProjectHeaderProps = {
  chapterMemoPolling: {
    chapters: Array<{
      id: number;
      hasActiveMemo: boolean;
      chapterName: string;
    }>;
  };
  hideTitle?: boolean;
  hideChapterTools?: boolean;
};

export function ProjectHeader(props: TProjectHeaderProps) {
  const { chapterMemoPolling, hideTitle, hideChapterTools } = props;
  const { t } = useTranslation(["projectHome", "common"]);
  const { openSidebar, toggleHandler, closeHandler } = useProjectSidebarStore();
  const { selectedProject, isFetchProjectLoading } = useProjectStore();
  const { activeChapter, isOpenChapterSidebar, toggleChapterSidebar, viewMode } = useChapterStore();
  const { isSidebarDocked } = useWorkspaceLayout();
  const { isLabs } = useMeStore();

  const showToolLabels = useMediaQuery("(min-width: 1332px)");

  const isLoading = isFetchProjectLoading;
  const { isSetupActive } = useProjectGenerationState(selectedProject ? String(selectedProject.id) : "");
  // Template-less (claude-code generation) projects get no labs menu and no value-chain layout toggle.
  const isTemplateLess = selectedProject?.projectTemplateSlug === "";

  // Check if the active chapter is loading (only during initial generation, not re-generation)
  const chapterMemoStatus = chapterMemoPolling.chapters.find((c) => c.id === Number(activeChapter?.id));
  const hasActiveMemo = chapterMemoStatus?.hasActiveMemo ?? true;

  // Per design the chapter tools stay off for the whole analysis, not just until the
  // chapter on screen is written.
  const areChapterToolsDisabled = !hasActiveMemo || isFetchProjectLoading || isSetupActive;

  // Close AQ/IQ sidebar when switching to a chapter where they're not available
  const chapterSlug = activeChapter?.projectTemplateChapter?.slug;
  useEffect(() => {
    const isAppendix = chapterSlug?.startsWith("appendix-");
    const isAqIqOpen = openSidebar === "analysis" || openSidebar === "intelligence-questions";

    if (isAppendix && isAqIqOpen) {
      closeHandler();
    }
  }, [chapterSlug, openSidebar, closeHandler]);

  const isChapterView = !viewMode || !NON_CHAPTER_VIEWS.has(viewMode);
  const isChapterLikeHeader = isChapterView || viewMode === "valueChain";
  const title = (() => {
    if (viewMode === "dashboard") return t("dashboard.title");
    if (viewMode === "projectOverview") return t("sidebar.home");
    if (viewMode === "valueChain") return t("sidebar.valueChain");
    if (!isChapterView) return selectedProject?.name ?? "";
    if (activeChapter && !isLoading) {
      // The two summary chapters are labelled from i18n in the sidebar, so keep the header in sync.
      if (chapterSlug === EXEC_SUMMARY_SLUG) return t(getExecSummaryLabelKey(isTemplateLess));
      if (chapterSlug === FULL_SUMMARY_SLUG) return t("sidebar.innoveraMemo");
      return activeChapter.projectTemplateChapter.name;
    }
    return t("common:loadingProject");
  })();
  const chapterGroupLabel = (() => {
    if (!activeChapter || !isChapterView) return null;
    if (isAppendixChapter(activeChapter)) return t("sidebar.appendices");
    if (activeChapter.isHidden) return t("sidebar.hiddenChapters");
    const section = DEEP_DIVE_SECTIONS.find((s) => s.key === activeChapter.projectTemplateChapter.section);
    return section ? t(section.labelKey) : null;
  })();

  return (
    <div data-testid="project-header">
      <div
        className={cn(
          "bg-white h-14 min-h-14 px-4 py-2 flex justify-between items-center border-b border-innoveraZinc-100",
          hideTitle && isSidebarDocked && "hidden"
        )}
      >
        <div className="flex items-center gap-4 justify-between w-full min-w-0">
          <div className="text-[14px] flex items-center gap-2 flex-1 min-w-0">
            {!isSidebarDocked && (
              <AppButton
                data-testid="project-header-sidebar-toggle"
                appearance="text"
                size="icon"
                startAdornment={isOpenChapterSidebar ? <XIcon /> : <ListIcon />}
                data-chapter-sidebar-toggle
                onClick={() => toggleChapterSidebar(!isOpenChapterSidebar)}
              />
            )}
            {viewMode === "dashboard" && !hideTitle && <DashboardHomeIcon />}
            {!hideTitle && (
              <div className="flex items-center gap-2 min-w-0">
                {!isLoading && chapterGroupLabel && (
                  <>
                    <FigmaTypography
                      variant="label-1"
                      color="zinc900"
                      className="min-w-0 whitespace-nowrap text-ellipsis overflow-hidden leading-[20px]"
                    >
                      {chapterGroupLabel}
                    </FigmaTypography>
                    <CaretRight className="size-3 shrink-0 text-innoveraZinc-500" weight="regular" />
                  </>
                )}
                <FigmaTypography
                  data-testid="project-title"
                  variant={isChapterLikeHeader ? "label-1" : "heading-3"}
                  className="min-w-0 max-w-[17vw] font-medium text-ellipsis overflow-hidden whitespace-nowrap leading-[20px]"
                >
                  {title}{" "}
                </FigmaTypography>

                {viewMode === "valueChain" && (
                  <span className="flex shrink-0 items-center gap-1 rounded-2xl border border-innoveraZinc-150 px-2.5 py-1">
                    <EyeIcon className="size-3.5 text-innoveraZinc-500" weight="regular" />
                    <FigmaTypography variant="label-3" color="zinc500">
                      {t("valueChain.readOnly")}
                    </FigmaTypography>
                  </span>
                )}

                {!isLoading && isLabs && <ProjectInfoTooltip />}
              </div>
            )}
          </div>
          <div className={cn("flex items-center gap-1 shrink-0")}>
            {viewMode === "valueChain" && !isTemplateLess && !hideChapterTools && <LayoutToggle />}
            {isChapterView && !hideChapterTools && (
              <>
                {getChapterTools({ commentCount: 0 }).map((option) => (
                  <AppButton
                    key={option.value}
                    data-testid={`sidebar-${option.value}-button`}
                    startAdornment={<option.icon />}
                    appearance="text"
                    className={cn(
                      "text-innoveraZinc-900 font-medium",
                      openSidebar === option.value && "bg-innoveraZinc-100"
                    )}
                    title={t(option.titleKey)}
                    onClick={() => toggleHandler(option.value)}
                    disabled={areChapterToolsDisabled}
                  >
                    {showToolLabels && t(option.labelKey)}
                  </AppButton>
                ))}
                <div className="mx-1 h-4 w-px bg-innoveraZinc-150" />
                <LayoutToggle disabled={areChapterToolsDisabled} />
                {!isTemplateLess && isLabs && <MemoOptionsMenu disabled={areChapterToolsDisabled} />}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
